import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuthResponse, Permission, PublicUser } from '@hutdrop/shared';
import { api } from './api';

interface StaffSession {
  user: PublicUser;
  permissions: Permission[];
}

interface StaffAuth {
  ready: boolean;
  session: AuthResponse | null;
  staff: StaffSession | undefined;
  can: (permission: Permission) => boolean;
  /** Set when the session is valid but the staff profile could not be loaded (e.g. offline). */
  loadError: Error | null;
  retry: () => void;
}

const Ctx = createContext<StaffAuth>({
  ready: false,
  session: null,
  staff: undefined,
  can: () => false,
  loadError: null,
  retry: () => {},
});

export function StaffAuthProvider({ children }: { children: ReactNode }) {
  const session = useSyncExternalStore(api.subscribe, api.getSession, () => null);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    api
      .restore()
      .catch(() => null)
      .finally(() => setRestored(true));
  }, []);

  const staffQuery = useQuery({
    queryKey: ['admin-session', session?.user.id],
    queryFn: () => api.request<StaffSession>('/v1/admin/session', { auth: true }),
    enabled: Boolean(session),
    staleTime: 5 * 60_000,
  });

  const staff = session ? staffQuery.data : undefined;
  const ready = restored && (!session || staffQuery.isFetched);
  const perms = new Set(staff?.permissions ?? []);

  return (
    <Ctx.Provider
      value={{
        ready,
        session,
        staff,
        can: (p) => perms.has(p),
        loadError: session ? staffQuery.error : null,
        retry: () => void staffQuery.refetch(),
      }}
    >{children}</Ctx.Provider>
  );
}

export function useStaffAuth() {
  return useContext(Ctx);
}
